import { useState, useEffect, useCallback } from 'react';
import { rmmAPI, usersAPI, sitesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, Network, Radar, KeySquare, Globe, Map as MapIcon, Power } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';
import { TopologyView } from './TopologyView';

const err = (e, m) => toast.error(e.response?.data?.detail || m);
const Spin = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
const StatusBadge = ({ s }) => (
  <Badge className={s === 'online' ? 'bg-emerald-100 text-emerald-700' : s === 'offline' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>{s || 'unknown'}</Badge>
);
const CLASSES = ['router', 'firewall', 'switch', 'wireless_ap', 'printer', 'ups', 'nas', 'server', 'camera', 'other'];

export const NetworkTab = ({ isAdmin }) => {
  const [devices, setDevices] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [topoSite, setTopoSite] = useState('');

  const load = useCallback(() => {
    Promise.all([rmmAPI.netDevices(), rmmAPI.snmpProfiles(), rmmAPI.discoveryJobs(), rmmAPI.websites()])
      .then(([d, p, j, w]) => { setDevices(d.data); setProfiles(p.data); setJobs(j.data); setWebsites(w.data); })
      .catch(() => setDevices([]));
  }, []);

  useEffect(() => {
    load();
    usersAPI.list('customer').then((r) => setCustomers(r.data)).catch(() => {});
    sitesAPI.list().then((r) => setSites(r.data)).catch(() => {});
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!devices) return <Spin />;
  const siteOptions = [...new Map([...devices.map((d) => [d.site_id, d.site_name]), ...sites.map((s) => [s.id, s.name])]).entries()].filter(([id]) => id);

  return (
    <div className="space-y-6" data-testid="rmm-network-tab">
      <NetDevices devices={devices} profiles={profiles} customers={customers} sites={sites} isAdmin={isAdmin} onChanged={load} />
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapIcon className="w-4 h-4 text-cyan-500" />Network Topology & Site Map</CardTitle></CardHeader>
        <CardContent>
          <Select value={topoSite} onValueChange={setTopoSite}>
            <SelectTrigger className="w-64 bg-white border-slate-200 mb-3" data-testid="rmm-topology-site-select"><SelectValue placeholder="Choose a site…" /></SelectTrigger>
            <SelectContent>{siteOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name || id}</SelectItem>)}</SelectContent>
          </Select>
          <TopologyView siteId={topoSite} />
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Discovery jobs={jobs} customers={customers} sites={sites} isAdmin={isAdmin} onChanged={load} />
        <SnmpProfiles profiles={profiles} isAdmin={isAdmin} onChanged={load} />
      </div>
      <Websites websites={websites} customers={customers} isAdmin={isAdmin} onChanged={load} />
    </div>
  );
};

// ===================== NETWORK DEVICES =====================
const NetDevices = ({ devices, profiles, customers, sites, isAdmin, onChanged }) => {
  const [form, setForm] = useState({ name: '', ip_address: '', device_class: 'switch', customer_id: '', site_id: '', parent_device_id: '', snmp_profile_id: '', tcp_ports: '', is_gateway: false });
  const custSites = sites.filter((s) => s.customer_id === form.customer_id);

  const add = () => {
    if (!form.name || !form.ip_address || !form.customer_id || !form.site_id) return toast.error('Name, IP, customer and site are required');
    rmmAPI.createNetDevice({
      ...form,
      parent_device_id: form.parent_device_id || null,
      snmp_profile_id: form.snmp_profile_id || null,
      tcp_ports: form.tcp_ports.split(',').map((p) => parseInt(p.trim(), 10)).filter((p) => p > 0 && p < 65536),
    }).then(() => { toast.success('Network device added — first poll within a minute'); setForm({ ...form, name: '', ip_address: '' }); onChanged(); }).catch((e) => err(e, 'Failed to add device'));
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Network className="w-4 h-4 text-cyan-500" />Network Devices <Badge variant="outline">{devices.length}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {isAdmin && (
          <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 items-center">
            <Input placeholder="Device name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" data-testid="net-dev-name-input" />
            <Input placeholder="IP address" value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} className="bg-white" data-testid="net-dev-ip-input" />
            <Select value={form.device_class} onValueChange={(v) => setForm({ ...form, device_class: v })}>
              <SelectTrigger className="bg-white" data-testid="net-dev-class-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, site_id: '' })}>
              <SelectTrigger className="bg-white" data-testid="net-dev-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })} disabled={!form.customer_id}>
              <SelectTrigger className="bg-white" data-testid="net-dev-site-select"><SelectValue placeholder="Site" /></SelectTrigger>
              <SelectContent>{custSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.snmp_profile_id || 'none'} onValueChange={(v) => setForm({ ...form, snmp_profile_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-white" data-testid="net-dev-snmp-select"><SelectValue placeholder="SNMP" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No SNMP</SelectItem>{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Ports (80,443)" value={form.tcp_ports} onChange={(e) => setForm({ ...form, tcp_ports: e.target.value })} className="bg-white" data-testid="net-dev-ports-input" />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                <Checkbox checked={form.is_gateway} onCheckedChange={(v) => setForm({ ...form, is_gateway: !!v })} data-testid="net-dev-gateway-checkbox" />Gateway
              </label>
              <Button size="sm" onClick={add} className="bg-cyan-600 hover:bg-cyan-700" data-testid="net-dev-add-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
        {devices.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No network devices yet — add one above or run discovery below.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Device</th><th className="pr-3">Class</th><th className="pr-3">IP</th><th className="pr-3">Site</th>
                <th className="pr-3">Status</th><th className="pr-3">Latency</th><th className="pr-3">Loss</th><th className="pr-3">Ports</th><th className="pr-3">SNMP</th><th className="pr-3">Checked</th><th /></tr></thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`net-dev-row-${d.hostname}`}>
                    <td className="py-2 pr-3 font-medium text-slate-800">{d.hostname}{d.is_gateway && <Badge variant="outline" className="ml-1.5 text-[10px]">GW</Badge>}{d.simulated && <Badge variant="outline" className="ml-1 text-[10px] text-violet-600 border-violet-200">sim</Badge>}</td>
                    <td className="pr-3 capitalize text-slate-600">{(d.device_class || '').replace('_', ' ')}</td>
                    <td className="pr-3 text-slate-600 font-mono text-xs">{d.ip_address}</td>
                    <td className="pr-3 text-slate-600">{d.site_name || '—'}</td>
                    <td className="pr-3"><StatusBadge s={d.status} /></td>
                    <td className="pr-3 text-slate-700">{d.latency_ms != null ? `${d.latency_ms} ms` : '—'}</td>
                    <td className="pr-3 text-slate-700">{d.packet_loss_pct != null ? `${d.packet_loss_pct}%` : '—'}</td>
                    <td className="pr-3">{Object.entries(d.port_status || {}).map(([p, ok]) => <Badge key={p} className={`mr-1 text-[10px] ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{p}</Badge>)}</td>
                    <td className="pr-3">{d.snmp_profile_id ? <Badge className={d.snmp_status === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{d.snmp_status || 'pending'}</Badge> : <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="pr-3 text-xs text-slate-500">{fmtWhen(d.last_checked)}</td>
                    <td>{isAdmin && <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" data-testid={`net-dev-delete-${d.hostname}`}
                      onClick={() => rmmAPI.deleteDevice(d.id).then(() => { toast.success('Removed'); onChanged(); }).catch((e) => err(e, 'Failed'))}><Trash2 className="w-3.5 h-3.5" /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===================== DISCOVERY =====================
const Discovery = ({ jobs, customers, sites, isAdmin, onChanged }) => {
  const [form, setForm] = useState({ customer_id: '', site_id: '', cidr: '', simulate: false });
  const [selected, setSelected] = useState({});
  const custSites = sites.filter((s) => s.customer_id === form.customer_id);

  const start = () => {
    if (!form.customer_id || !form.site_id || !form.cidr) return toast.error('Customer, site and CIDR are required');
    rmmAPI.startDiscovery(form).then(() => { toast.success('Discovery started — refresh in a few seconds'); setTimeout(onChanged, 4000); }).catch((e) => err(e, 'Failed to start discovery'));
  };
  const enroll = (job) => {
    const ips = Object.entries(selected[job.id] || {}).filter(([, v]) => v).map(([ip]) => ip);
    if (!ips.length) return toast.error('Select at least one discovered device');
    rmmAPI.enrollDiscovered(job.id, ips).then((r) => { toast.success(`Enrolled ${r.data.enrolled.length} device(s)`); onChanged(); }).catch((e) => err(e, 'Enroll failed'));
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Radar className="w-4 h-4 text-cyan-500" />Network Discovery</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {isAdmin && (
          <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, site_id: '' })}>
                <SelectTrigger className="bg-white" data-testid="disc-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })} disabled={!form.customer_id}>
                <SelectTrigger className="bg-white" data-testid="disc-site-select"><SelectValue placeholder="Site" /></SelectTrigger>
                <SelectContent>{custSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 items-center">
              <Input placeholder="CIDR e.g. 192.168.1.0/24 (private ranges only)" value={form.cidr} onChange={(e) => setForm({ ...form, cidr: e.target.value })} className="bg-white" data-testid="disc-cidr-input" />
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                <Checkbox checked={form.simulate} onCheckedChange={(v) => setForm({ ...form, simulate: !!v })} data-testid="disc-simulate-checkbox" />Demo
              </label>
              <Button size="sm" onClick={start} className="bg-cyan-600 hover:bg-cyan-700" data-testid="disc-start-btn">Scan</Button>
            </div>
          </div>
        )}
        {jobs.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No discovery scans yet.</p> : jobs.slice(0, 3).map((j) => (
          <div key={j.id} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`disc-job-${j.cidr}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-800">{j.cidr} <span className="text-slate-500 font-normal">· {j.site_name}</span></span>
              <Badge className={j.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : j.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{j.status} {j.status === 'completed' && `· ${j.found} found`}</Badge>
            </div>
            {j.status === 'completed' && j.results?.length > 0 && (
              <>
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {j.results.map((r) => (
                    <label key={r.ip} className="flex items-center gap-2 text-xs text-slate-700 p-1.5 rounded hover:bg-slate-50">
                      <Checkbox disabled={r.already_enrolled} checked={!!selected[j.id]?.[r.ip]}
                        onCheckedChange={(v) => setSelected({ ...selected, [j.id]: { ...(selected[j.id] || {}), [r.ip]: !!v } })}
                        data-testid={`disc-result-check-${r.ip}`} />
                      <span className="font-mono">{r.ip}</span>
                      <span className="font-medium">{r.hostname || '—'}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{(r.device_type || 'other').replace('_', ' ')}</Badge>
                      <span className="text-slate-400">ports: {(r.open_ports || []).join(', ') || '—'}</span>
                      {r.already_enrolled && <Badge className="bg-slate-100 text-slate-500 text-[10px]">enrolled</Badge>}
                    </label>
                  ))}
                </div>
                {isAdmin && <Button size="sm" variant="outline" onClick={() => enroll(j)} data-testid="disc-enroll-btn">Enroll selected</Button>}
              </>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ===================== SNMP PROFILES =====================
const SnmpProfiles = ({ profiles, isAdmin, onChanged }) => {
  const [form, setForm] = useState({ name: '', version: 'v3', community: '', v3_user: '', v3_auth_key: '', v3_priv_key: '', v3_auth_protocol: 'sha', v3_priv_protocol: 'aes' });
  const add = () => {
    if (!form.name) return toast.error('Profile name required');
    rmmAPI.createSnmpProfile(form).then(() => { toast.success('SNMP profile saved (secrets encrypted)'); setForm({ ...form, name: '', community: '', v3_user: '', v3_auth_key: '', v3_priv_key: '' }); onChanged(); }).catch((e) => err(e, 'Failed'));
  };
  return (
    <Card className="bg-white border-slate-200">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeySquare className="w-4 h-4 text-cyan-500" />SNMP Profiles <Badge variant="outline">{profiles.length}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {isAdmin && (
          <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 space-y-2">
            <div className="flex gap-2">
              <Input placeholder="Profile name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" data-testid="snmp-name-input" />
              <Select value={form.version} onValueChange={(v) => setForm({ ...form, version: v })}>
                <SelectTrigger className="w-28 bg-white" data-testid="snmp-version-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="v3">SNMPv3</SelectItem><SelectItem value="v2c">SNMPv2c</SelectItem></SelectContent>
              </Select>
            </div>
            {form.version === 'v2c' ? (
              <Input type="password" placeholder="Community string" value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} className="bg-white" data-testid="snmp-community-input" />
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Username" value={form.v3_user} onChange={(e) => setForm({ ...form, v3_user: e.target.value })} className="bg-white" data-testid="snmp-v3user-input" />
                <Input type="password" placeholder="Auth key (SHA)" value={form.v3_auth_key} onChange={(e) => setForm({ ...form, v3_auth_key: e.target.value })} className="bg-white" data-testid="snmp-authkey-input" />
                <Input type="password" placeholder="Privacy key (AES)" value={form.v3_priv_key} onChange={(e) => setForm({ ...form, v3_priv_key: e.target.value })} className="bg-white" data-testid="snmp-privkey-input" />
              </div>
            )}
            <Button size="sm" onClick={add} className="bg-cyan-600 hover:bg-cyan-700" data-testid="snmp-add-btn"><Plus className="w-4 h-4 mr-1" />Save profile</Button>
            <p className="text-[11px] text-slate-500">Secrets are encrypted at rest and never shown again. SNMPv3 is preferred.</p>
          </div>
        )}
        {profiles.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No SNMP profiles yet.</p> : profiles.map((p) => (
          <div key={p.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg border border-slate-200" data-testid={`snmp-profile-${p.name}`}>
            <div>
              <span className="font-medium text-slate-800">{p.name}</span>
              <span className="text-xs text-slate-500 ml-2">{p.version.toUpperCase()} · port {p.port}{p.v3_user ? ` · user ${p.v3_user}` : ''}{p.has_community ? ' · community ••••' : ''}{p.has_auth_key ? ' · auth ••••' : ''}</span>
            </div>
            {isAdmin && <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" data-testid={`snmp-delete-${p.name}`}
              onClick={() => rmmAPI.deleteSnmpProfile(p.id).then(() => { toast.success('Deleted'); onChanged(); }).catch((e) => err(e, 'Failed — profile may be in use'))}><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ===================== WEBSITES =====================
const Websites = ({ websites, customers, isAdmin, onChanged }) => {
  const [form, setForm] = useState({ url: '', label: '', customer_id: '', ssl_warn_days: 30 });
  const add = () => {
    if (!form.url) return toast.error('URL required');
    rmmAPI.createWebsite({ ...form, customer_id: form.customer_id || null }).then(() => { toast.success('Website monitor added'); setForm({ ...form, url: '', label: '' }); onChanged(); }).catch((e) => err(e, 'Failed'));
  };
  const sslBadge = (w) => {
    if (w.ssl_days_left == null) return <span className="text-xs text-slate-400">—</span>;
    const warn = w.ssl_warn_days || 30;
    const cls = w.ssl_days_left <= 7 ? 'bg-red-100 text-red-700' : w.ssl_days_left <= warn ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
    return <Badge className={cls}>{w.ssl_days_left}d</Badge>;
  };
  return (
    <Card className="bg-white border-slate-200">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-500" />Website, DNS & Certificate Monitoring <Badge variant="outline">{websites.length}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {isAdmin && (
          <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
            <Input placeholder="https://example.com" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-white md:col-span-2" data-testid="web-url-input" />
            <Input placeholder="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-white" data-testid="web-label-input" />
            <Select value={form.customer_id || 'none'} onValueChange={(v) => setForm({ ...form, customer_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-white" data-testid="web-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Internal / none</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" onClick={add} className="bg-cyan-600 hover:bg-cyan-700" data-testid="web-add-btn"><Plus className="w-4 h-4 mr-1" />Monitor</Button>
          </div>
        )}
        {websites.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No websites monitored yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Website</th><th className="pr-3">Status</th><th className="pr-3">HTTP</th><th className="pr-3">Response</th><th className="pr-3">DNS</th><th className="pr-3">SSL expiry</th><th className="pr-3">Checked</th><th /></tr></thead>
              <tbody>
                {websites.map((w) => (
                  <tr key={w.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`web-row-${w.url}`}>
                    <td className="py-2 pr-3">
                      <span className="font-medium text-slate-800">{w.label || w.url}</span>
                      {w.label && <span className="text-xs text-slate-400 ml-1.5">{w.url}</span>}
                      {!w.enabled && <Badge variant="outline" className="ml-1.5 text-[10px]">paused</Badge>}
                    </td>
                    <td className="pr-3">{w.last_status ? <Badge className={w.last_status === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{w.last_status}</Badge> : <span className="text-xs text-slate-400">pending</span>}</td>
                    <td className="pr-3 text-slate-600">{w.http_status_code || '—'}</td>
                    <td className="pr-3 text-slate-600">{w.response_ms != null ? `${w.response_ms} ms` : '—'}</td>
                    <td className="pr-3">{w.dns_ok == null ? <span className="text-xs text-slate-400">—</span> : <Badge className={w.dns_ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{w.dns_ok ? 'ok' : 'fail'}</Badge>}</td>
                    <td className="pr-3">{sslBadge(w)}</td>
                    <td className="pr-3 text-xs text-slate-500">{fmtWhen(w.last_checked)}</td>
                    <td className="whitespace-nowrap">
                      {isAdmin && <>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500" title="Pause/resume" data-testid={`web-toggle-${w.url}`}
                          onClick={() => rmmAPI.toggleWebsite(w.id).then(onChanged)}><Power className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" data-testid={`web-delete-${w.url}`}
                          onClick={() => rmmAPI.deleteWebsite(w.id).then(() => { toast.success('Removed'); onChanged(); }).catch((e) => err(e, 'Failed'))}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
